import { useQueryClient } from "@tanstack/react-query";
import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { clearSession } from "../api/auth";
import { isPlatformAdminFlag, setPlatformAdminFlag } from "../api/platform";
import { Button } from "@/components/ui/button";

/**
 * Minimal chrome for the SaaS platform super-admin console (ADR-0021). Deliberately
 * separate from the clinic {@code AppShell} — the platform admin manages tenants and
 * never sees clinic navigation or branding.
 *
 * The real authorization gate is the backend (permission + platform-realm assertion);
 * this flag-based guard only keeps a non-platform user from landing on the console UI.
 */
export function PlatformShell() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  if (!isPlatformAdminFlag()) {
    return <Navigate to="/dashboard" replace />;
  }

  function logout() {
    clearSession();
    setPlatformAdminFlag(false);
    qc.clear();
    navigate("/login");
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between gap-3 bg-foreground px-4 py-3 text-background sm:px-6">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
          <strong className="text-sm font-bold">Addy Platform</strong>
          <span className="text-xs text-background/70">
            Quản trị nền tảng · Toàn bộ phòng khám
          </span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={logout}
          className="shrink-0 text-background hover:bg-background/10 hover:text-background"
        >
          <LogOut aria-hidden="true" />
          Đăng xuất
        </Button>
      </header>
      {/* Console nền tảng trước đây chỉ có một trang nên không cần điều hướng. Từ khi có trang
          thứ hai, thiếu nó nghĩa là chỉ tới được bằng cách gõ URL. */}
      <nav className="border-b bg-muted/30">
        <div className="mx-auto flex w-full max-w-6xl gap-1 px-4 sm:px-6">
          {[
            { to: "/platform/tenants", label: "Phòng khám" },
            { to: "/platform/zalo", label: "Cấu hình Zalo" },
            { to: "/platform/thanh-toan", label: "Thanh toán" },
            { to: "/platform/thu-tien", label: "Tiền vào" },
            { to: "/platform/doi-soat", label: "Đối soát" },
            { to: "/platform/goi-dich-vu", label: "Gói & bảng giá" },
            { to: "/platform/gia-hop-dong", label: "Giá hợp đồng" },
            { to: "/platform/hoa-don", label: "Hoá đơn" },
            { to: "/platform/hoa-don-dien-tu", label: "HĐ điện tử" },
            { to: "/platform/variables", label: "Biến & Alias" },
          ].map((i) => (
            <NavLink
              key={i.to}
              to={i.to}
              className={({ isActive }) =>
                [
                  "border-b-2 px-3 py-2 text-sm transition-colors",
                  isActive
                    ? "border-primary font-medium text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                ].join(" ")
              }
            >
              {i.label}
            </NavLink>
          ))}
        </div>
      </nav>
      <main className="mx-auto w-full max-w-6xl p-4 sm:p-6">
        <Outlet />
      </main>
    </div>
  );
}
